// Filter keys that participate in permalink canonicalization on the backend
const SEO_RELEVANT_PARAM_KEYS = [
  'fandom',
  'fandoms',
  'tags',
  'characters',
  'pairing',
  'pairingCharacters',
];

// A pairing is inherently a set of 2+ character ids — that is not a
// "complex intersection" the way fandoms=1,2 is.
const MULTI_VALUE_ALLOWED_KEYS = new Set(['pairing', 'pairingCharacters']);

const hasMultipleValues = (value: string | string[] | undefined): boolean => {
  if (!value) return false;
  if (Array.isArray(value)) return value.length > 1;
  return value.split(',').filter(Boolean).length > 1;
};

const normalizeSeoDimension = (key: string): string => {
  if (key === 'fandom' || key === 'fandoms') return 'fandom';
  if (key === 'pairing' || key === 'pairingCharacters') return 'pairing';
  return key;
};

/**
 * Determines whether the current filter combination is a "complex intersection"
 * that should NOT be auto-canonicalized into a permanent SEO URL, per requirements:
 * - multiple values in any single SEO-relevant param (e.g. fandoms=1,2)
 * - multiple different SEO-relevant params combined at once (e.g. characters + tags + rating)
 * In these cases the page must stay on GET-filter logic with noindex,nofollow.
 */
export const isComplexFilterIntersection = (
  params: Record<string, string | string[] | undefined>,
): boolean => {
  const presentSeoKeys = SEO_RELEVANT_PARAM_KEYS.filter((key) => Boolean(params[key]));

  const hasAnyMultiValue = presentSeoKeys.some(
    (key) => !MULTI_VALUE_ALLOWED_KEYS.has(key) && hasMultipleValues(params[key]),
  );
  if (hasAnyMultiValue) return true;

  // More than one distinct SEO-relevant filter dimension combined simultaneously
  // (fandom + characters is a normal single-canonical case handled by backend;
  // anything beyond fandom+characters or fandom+pairing is considered complex)
  const distinctDimensions = new Set(presentSeoKeys.map(normalizeSeoDimension));

  if (distinctDimensions.has('tags') && distinctDimensions.size > 1) return true;
  if (distinctDimensions.size > 2) return true;

  return false;
};
